'use client';

import { useQuantity } from '~/hooks/product/use-quantity';
import { type Product } from './product-details';
import { QuantityInput } from '../basket/quantity-input';
import { Button } from '../ui/button';
import { AddToBasketButton } from './add-to-basket-button';
import { InlineLink } from '../common/inline-link';
import { ExternalLinkIcon } from 'lucide-react';
import { NotifyInStock } from './product-notify-in-stock';
import { ProductPriceBreaks } from './product-price-breaks';
import { type LinkPickerValue } from '~/utils/content-api-utils/content-helpers';
import { memo, type ReactNode } from 'react';
import { gtmAddProductsToCart } from '~/utils/gtm/gtm-helpers';

export type ProductDetailsAddtoBasketProps = {
  product: Product;
  isAuthed?: boolean;
  className?: string;
  variant: 'mobile' | 'desktop';
  restrictedHowToBuyLink?: LinkPickerValue | null;
  productVariants?: ReactNode;
};

export const ProductDetailsAddtoBasket = memo(
  ({
    product,
    isAuthed = false,
    variant,
    restrictedHowToBuyLink,
    productVariants,
  }: ProductDetailsAddtoBasketProps) => {
    const { onQuantityChange, minQuantity, quantity, isLoading } = useQuantity({
      catalogueRef: product.catalogueRef,
      minimumOrderQuantity: product.minOrderQuantity,
    });

    const components: ReactNode[] = [
      <div
        key="input-area"
        className="!mt-6 flex flex-wrap items-center gap-6 sm:!mt-8"
      >
        <QuantityInput
          size="pdp"
          className="max-w-[120px]"
          disabled={product.isRestricted || product.isTemporarilyUnavailable}
          onChange={onQuantityChange}
          minQuantity={minQuantity}
          quantity={quantity}
          isLoading={isLoading}
        />
        {product.isRestricted || product.isTemporarilyUnavailable ? (
          <Button className="grow md:max-w-[11rem]" disabled>
            {product.isRestricted ? 'Restricted Product' : 'Back Soon'}
          </Button>
        ) : (
          <AddToBasketButton
            className={`pdp-add-to-basket grow md:max-w-[11rem]`}
            itemCode={product.catalogueRef}
            productName={product.productName}
            quantity={quantity}
            onSuccess={() => {
              gtmAddProductsToCart({
                products: [[product, quantity]],
                currency: 'GBP',
                itemListId: 'pdp',
                itemListName: 'PDP',
              });
            }}
          />
        )}
        {/* How to Order */}
        {!!product.isRestricted &&
          !!restrictedHowToBuyLink && ( // TODO: add link to restricted products page (from CMS?)
            <div className="flex h-11 w-full items-center justify-center border-b border-current text-center xl:w-auto">
              <InlineLink
                href={restrictedHowToBuyLink.href}
                className="inline-flex items-center gap-2 px-4 no-underline hover:underline"
                variant="inherit"
                target={restrictedHowToBuyLink.target}
              >
                {restrictedHowToBuyLink.title}{' '}
                {restrictedHowToBuyLink.target === '_blank' ? (
                  <ExternalLinkIcon className="size-5" />
                ) : null}
              </InlineLink>
            </div>
          )}

        <NotifyInStock
          isAuthed={isAuthed}
          isOutOfStock={product.stockInfo.stockLevel === 0}
          isStockRelevant={product.stockInfo.isStockRelevant}
          itemCode={product.catalogueRef}
        />
      </div>,
    ];
    const priceBreaks = product.hasPriceBreak ? (
      <ProductPriceBreaks
        key="price-breaks"
        priceBreaks={product.priceBreaks}
        disabled={product.isRestricted || product.isTemporarilyUnavailable}
        onPriceBreakClick={(quantity) => {
          onQuantityChange(quantity);
        }}
      />
    ) : null;

    if (priceBreaks) {
      if (variant === 'mobile') {
        components.push(priceBreaks);
      } else {
        components.unshift(priceBreaks);
      }
    }

    if (productVariants) {
      components.push(productVariants);
    }

    return components;
  },
);

ProductDetailsAddtoBasket.displayName = 'ProductDetailsAddtoBasket';
